import { useQuery } from '@tanstack/react-query';
import { dataSelector } from '@nextiva/global-store';
import { useSelector } from 'react-redux';
import { isBoolean, omit } from 'lodash';
import { AxiosError } from 'axios';
import { getEnablementInfo } from 'services';
import { FormValuesV3 } from 'interface/WizardV3';
import { useSuggestedContactEmail } from 'hooks/useSuggestedContactEmail';
import {
  BRAND_REGISTRATION_FIELDS,
  CUSTOMER_PROMPTS_FIELDS,
  ENABLEMENT_INFO_KEY,
} from '../constants';

export function useGetEnablementInfo(methods: any) {
  const globalStoreData = useSelector(dataSelector);
  const corpAcctNbr = globalStoreData.user?.profileDTO?.corpAcctNbr;
  const suggestedContactEmail = useSuggestedContactEmail();
  const enabled = !!corpAcctNbr;

  const { getValues } = methods;

  const {
    data: enablementInfo,
    isLoading: isLoadingGetEnablementInfo,
    refetch,
  } = useQuery([ENABLEMENT_INFO_KEY], {
    queryFn: () => getEnablementInfo(corpAcctNbr),
    enabled,
    onSuccess: (res) => {
      if (res) {
        // STATIC DEFAULT VALUES
        // Initialize form's default values with enablementInfo from server. These are "static" because they don't depend on input from other fields)
        // If the default value depends on user input from another field, it should be set differently using useEffect and setValue('field', value)
        const staticDefaultValues = omit(res, [
          CUSTOMER_PROMPTS_FIELDS.OPT_IN_MESSAGE,
          CUSTOMER_PROMPTS_FIELDS.OPT_OUT_MESSAGE,
          CUSTOMER_PROMPTS_FIELDS.SUPPORT_MESSAGE,
        ]);
        const defaultFormValues: FormValuesV3 = {
          ...staticDefaultValues,
          addressCountry: 'US',
          isSameAsBillingAddress: false, // Disabled for now. When enabled, we can use response.isSameAsBillingAddress || false.

          isDefaultStartKeyword: isBoolean(res.isDefaultStartKeyword)
            ? res.isDefaultStartKeyword
            : true,
          isDefaultStopKeyword: isBoolean(res.isDefaultStopKeyword)
            ? res.isDefaultStopKeyword
            : true,
          isDefaultHelpKeyword: isBoolean(res.isDefaultHelpKeyword)
            ? res.isDefaultHelpKeyword
            : true,
          optinKeywords: res.optinKeywords || 'START',
          optoutKeywords: res.optoutKeywords || 'STOP',
          helpKeywords: res.helpKeywords || 'HELP',
          email: res.email || suggestedContactEmail,
        };

        methods.reset({ ...getValues(), ...defaultFormValues });
        if (
          getValues(BRAND_REGISTRATION_FIELDS.COMPANY_TYPE) !==
          'SOLE_PROPRIETOR'
        ) {
          if (!getValues(BRAND_REGISTRATION_FIELDS.TAX_ID)) {
            methods.setError(BRAND_REGISTRATION_FIELDS.TAX_ID, {
              type: 'required',
              message: 'Tax ID is required',
            });
          }
          if (!getValues(BRAND_REGISTRATION_FIELDS.TAX_ID_CONFIRM)) {
            methods.setError(BRAND_REGISTRATION_FIELDS.TAX_ID_CONFIRM, {
              type: 'required',
              message: 'Tax ID is required',
            });
          }
        }
      }
    },
    onError: (e) => {
      if (!(e instanceof AxiosError) || e.response?.status !== 404) {
        // eslint-disable-next-line no-console
        console.error('Error in useGetEnablementInfo', e);
      }
    },
  });
  return { enablementInfo, isLoadingGetEnablementInfo, refetch };
}
